import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { clearAuthDataAction, setDataAndTokenAction } from '@/redux/slicers/auth.slice';
import { setListSidebarAction } from '@/redux/slicers/sidebar.slice';
import { Messages } from '@/constants/message';
import { useRootSelector } from './selector.hook';
import {
  setToken,
  setRefreshToken,
  removeToken,
  removeRefreshToken,
  getToken,
  getRefreshToken,
  isTokenExpired,
  isRefreshTokenExpired,
  setFactoryId,
  setManagerId,
  removeFactoryId,
  removeManagerId,
} from '@/utils/common';
import { useApi, useCaller } from '@/hooks/api.hook';

// Global state để tránh duplicate refresh calls
let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;

// Global state để tránh duplicate profile fetch calls
let isFetchingProfile = false;
let profilePromise: Promise<any> | null = null;

// Global refresh function để đảm bảo chỉ gọi 1 lần
const globalRefreshToken = async (api: any, caller: any) => {
  // Nếu đang refresh, return promise hiện tại
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  // Bắt đầu refresh
  isRefreshing = true;

  refreshPromise = (async () => {
    try {
      const refreshToken = getRefreshToken();

      if (!refreshToken || isRefreshTokenExpired(refreshToken)) {
        throw new Error('No valid refresh token');
      }

      const { content, succeeded } = await caller(
        () =>
          api.post(`/api/v1/auths/sessions/refresh`, {
            refreshToken: refreshToken,
          }),
        {
          loadingKey: 'refresh-token-loading',
        },
      );

      if (succeeded && content) {
        const { accessToken: newAccessToken, refreshToken: newRefreshToken } = content;

        if (newAccessToken) {
          // Lưu token mới vào local
          setToken(newAccessToken);
          if (newRefreshToken) {
            setRefreshToken(newRefreshToken);
          }

          return {
            success: true,
            accessToken: newAccessToken,
            refreshToken: newRefreshToken || refreshToken,
          };
        }
      }

      throw new Error('Refresh failed');
    } catch (error) {
      console.error('❌ Global refresh error:', error);
      throw error;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Global fetch profile function để đảm bảo chỉ gọi 1 lần
const globalFetchProfile = async (api: any, caller: any, dispatch: any, currentUser: any) => {
  // Nếu đã có user, không cần fetch
  if (currentUser) {
    return { success: true, user: currentUser };
  }

  // Nếu đang fetch, return promise hiện tại
  if (isFetchingProfile && profilePromise) {
    return profilePromise;
  }

  // Bắt đầu fetch
  isFetchingProfile = true;

  profilePromise = (async () => {
    try {
      const result = await caller(() => api.get('/api/v1/users/me'), {
        loadingKey: 'profile-loading',
      });

      if (result?.succeeded) {
        dispatch(
          setDataAndTokenAction({
            user: result.content,
          }),
        );
        return { success: true, user: result.content };
      }

      return { success: false };
    } catch (error) {
      console.error('❌ Global fetch profile error:', error);
      return { success: false };
    } finally {
      isFetchingProfile = false;
      profilePromise = null;
    }
  })();

  return profilePromise;
};

export const useAuth = () => {
  const dispatch = useDispatch();
  const api = useApi('');
  const caller = useCaller();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user } = useRootSelector((state) => state.auth);

  const clearData = useCallback(() => {
    removeToken();
    removeRefreshToken();
    removeFactoryId();
    removeManagerId();
    dispatch(clearAuthDataAction());
    navigate('/login');
  }, [dispatch, navigate]);

  const fetchProfile = useCallback(async () => {
    // Sử dụng global fetch function
    await globalFetchProfile(api, caller, dispatch, user);
  }, [api, caller, dispatch, user]);

  const login = useCallback(
    async (email: string, password: string) => {
      const { content, succeeded } = await caller(
        () =>
          api.post(`/api/v1/auths/sessions/login`, {
            email,
            password,
          }),
        {
          loadingKey: 'login-loading',
          successMessage: Messages.LOGIN_SUCCESS,
        },
      );

      if (succeeded) {
        let token = content?.tokens?.accessToken;
        let refreshToken = content?.tokens?.refreshToken;
        const loginUser = content?.user;

        // Lưu token ban đầu vào localStorage
        setToken(token);
        setRefreshToken(refreshToken);

        // Lưu factoryId và managerId (nếu là manager) vào localStorage
        if (loginUser?.factoryId) {
          setFactoryId(loginUser.factoryId);
        }
        if (loginUser?.isManager && loginUser?.id) {
          setManagerId(loginUser.id);
        }

        // Check xem token có hết hạn không
        if (token && isTokenExpired(token)) {
          // Token đã hết hạn -> cần refresh trước khi call profile
          if (refreshToken && !isRefreshTokenExpired(refreshToken)) {
            try {
              // Call refresh API sử dụng global refresh function
              const result = await globalRefreshToken(api, caller);

              if (result.success) {
                // Refresh thành công -> lưu token mới vào local (thay thế cũ)
                token = result.accessToken;
                refreshToken = result.refreshToken;

                // Đảm bảo token mới đã được lưu vào localStorage
                setToken(token);
                setRefreshToken(refreshToken);

                // Update Redux với token mới
                dispatch(
                  setDataAndTokenAction({
                    token,
                    refreshToken,
                  }),
                );
              } else {
                // Refresh thất bại -> đá về trang login
                clearData();
                return;
              }
            } catch (error) {
              // Refresh thất bại -> đá về trang login
              console.error('Token refresh failed after login:', error);
              clearData();
              return;
            }
          } else {
            // Refresh token không hợp lệ -> đá về trang login
            clearData();
            return;
          }
        } else {
          // Token còn hạn -> update Redux với token hiện tại
          dispatch(
            setDataAndTokenAction({
              token,
              refreshToken,
            }),
          );
        }

        // Sau khi đảm bảo token hợp lệ, mới call profile
        // Đảm bảo token đã được lưu vào localStorage trước khi call profile
        // Đọc lại token từ localStorage để verify
        const savedToken = getToken();
        if (savedToken) {
          // Token đã được lưu -> call profile
          await fetchProfile();
        } else {
          // Token chưa được lưu -> đợi một chút rồi thử lại
          await new Promise((resolve) => setTimeout(resolve, 100));
          const retryToken = getToken();
          if (retryToken) {
            await fetchProfile();
          } else {
            console.error('Token not saved after login');
            clearData();
            return;
          }
        }

        // Fetch sidebar data to determine first permitted module
        const sidebarResult = await caller(
          () => api.get(`/api/v1/users/me/authorization`),
          { loadingKey: 'get-sidebar-after-login' },
        );

        if (sidebarResult?.succeeded && sidebarResult.content?.navigation?.length > 0) {
          dispatch(setListSidebarAction({ data: sidebarResult.content }));
          // Navigate directly to the first permitted module
          navigate(sidebarResult.content.navigation[0].url);
        } else {
          // Fallback: navigate to root, SmartRedirect will handle
          navigate('/');
        }
      }
    },
    [api, caller, clearData, dispatch, fetchProfile, navigate],
  );

  // DEPRECATED: refreshToken function removed
  // Axios interceptor now handles all token refresh automatically
  // This prevents duplicate refresh calls and race conditions

  const checkAuthStatus = useCallback(async () => {
    try {
      const token = getToken();
      const refreshToken = getRefreshToken();

      // Nếu không có token, user chưa đăng nhập
      if (!token) {
        dispatch(clearAuthDataAction());
        return false;
      }

      // Nếu token còn hạn, user đã đăng nhập
      if (!isTokenExpired(token)) {
        dispatch(
          setDataAndTokenAction({
            isAuthenticated: true,
            token,
            refreshToken,
          }),
        );

        // Fetch profile để load user info
        await fetchProfile();

        return true;
      }

      // Token hết hạn - kiểm tra refresh token
      if (!refreshToken || isRefreshTokenExpired(refreshToken)) {
        clearData();
        return false;
      }

      // Refresh token còn hạn - sử dụng global refresh function
      try {
        const result = await globalRefreshToken(api, caller);

        if (result.success) {
          // Update Redux
          dispatch(
            setDataAndTokenAction({
              isAuthenticated: true,
              token: result.accessToken,
              refreshToken: result.refreshToken,
            }),
          );

          // Fetch profile sau khi refresh token thành công
          await fetchProfile();

          return true;
        }

        // Refresh thất bại
        clearData();
        return false;
      } catch (refreshError) {
        console.error('Token refresh error:', refreshError);
        clearData();
        return false;
      }
    } catch (error) {
      console.error('Auth check error:', error);
      clearData();
      return false;
    }
  }, [api, caller, clearData, dispatch, fetchProfile]);

  const logout = useCallback(async () => {
    try {
      const refreshToken = getRefreshToken();

      if (refreshToken) {
        // Gọi API logout để invalidate refresh token ở server
        await caller(
          () =>
            api.post(`/api/v1/auths/sessions/logout`, {
              refreshToken: refreshToken,
            }),
          {
            loadingKey: 'logout-loading',
            successMessage: Messages.LOGOUT_SUCCESS,
          },
        );
      }
    } catch (error) {
      console.error('Logout API error:', error);
      // Vẫn tiếp tục clear data ở client dù API call thất bại
    } finally {
      // Luôn clear data ở client sau khi logout
      clearData();
    }
  }, [api, caller, clearData]);

  return {
    login,
    logout,
    clearData,
    checkAuthStatus,
    isAuthenticated,
    isLoading,
    fetchProfile,
  };
};

// Export global refresh function để sử dụng ở nơi khác
export { globalRefreshToken, globalFetchProfile };
